import { createCache } from './cache';

export type SuspenseCacheSingletonState = 'unstarted' | 'started' | 'ready' | 'failed';

export interface SuspenseCacheSingleton<Input, Result> {
    state: SuspenseCacheSingletonState;
    read: (input: Input) => Result;
    readAsync: (input: Input) => Promise<Result>;
    write: (input: Input, result: Result) => void;
    clear: (input: Input) => void;
}

/**
 * Create a suspense cache singleton.
 */
export function createCacheSingleton<Input, Result>(
    fn: (input: Input) => Result | Promise<Result>
): SuspenseCacheSingleton<Input, Result> {
    const cache = createCache<Input, Result>({
        getKey: () => '',
        getValue: fn,
    });

    return {
        get state(): SuspenseCacheSingletonState {
            // @ts-ignore - `null` works as an Input because the cache key will always be the same
            const cacheState = cache.getCacheState(null);
            if (!cacheState) {
                return 'unstarted';
            }
            if ('result' in cacheState) {
                return 'ready';
            }
            if ('error' in cacheState) {
                return 'failed';
            }
            return 'started';
        },
        read: cache.read,
        readAsync: cache.readAsync,
        write: cache.write,
        clear: cache.clear,
    };
}
